import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Brain, Lightbulb, ArrowRight, Target, Zap, Scale, RotateCcw, Shield, 
  User, Users, LogOut, FileText, Sparkles, Award, ChevronRight,
  GraduationCap, Globe, Play, CheckCircle, Star, TrendingUp
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { usePersona } from '../contexts/PersonaContext';
import { trackPageView } from '../utils/analytics';
import AppFooter from '../components/AppFooter';

const HomePage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { openPersonaModal } = usePersona();
  const [activeStage, setActiveStage] = useState(0);
  
  useEffect(() => {
    trackPageView('/');
  }, []);

  // Auto-rotate stages
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStage((prev) => (prev + 1) % 6);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const stages = [
    { key: 'spark', icon: Lightbulb, title: 'Spark Activation', desc: 'Generate initial ideas through divergent thinking', color: 'from-amber-400 to-orange-500', bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700' },
    { key: 'mirror', icon: Target, title: 'Mirror Validation', desc: 'Check against existing innovations and prior art', color: 'from-cyan-400 to-blue-500', bg: 'bg-cyan-50', border: 'border-cyan-200', text: 'text-cyan-700' },
    { key: 'oi', icon: Scale, title: 'Originality Index', desc: 'Score across four dimensions of originality', color: 'from-violet-400 to-purple-500', bg: 'bg-violet-50', border: 'border-violet-200', text: 'text-violet-700' },
    { key: 'msa', icon: Zap, title: 'Mirror-Spark Assessment', desc: 'Distinguish human creativity from AI mimicry', color: 'from-emerald-400 to-teal-500', bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700' },
    { key: 'iqi', icon: Shield, title: 'Interaction Quality', desc: 'Assess quality of your creative process', color: 'from-pink-400 to-rose-500', bg: 'bg-pink-50', border: 'border-pink-200', text: 'text-pink-700' },
    { key: 'iterate', icon: RotateCcw, title: 'Iteration Cycle', desc: 'Refine ideas based on feedback and scores', color: 'from-indigo-400 to-blue-500', bg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-700' }
  ];

  const stats = [
    { value: '50K+', label: 'Ideas Evaluated', icon: Sparkles },
    { value: '12K+', label: 'Active Innovators', icon: Users },
    { value: '94%', label: 'Satisfaction Rate', icon: Star },
    { value: '150+', label: 'Institutions', icon: GraduationCap }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white text-gray-900 overflow-hidden">
      {/* Subtle Background Accents */}
      <div className="fixed top-0 left-1/4 w-[600px] h-[600px] bg-violet-200/30 blur-[150px] rounded-full pointer-events-none" />
      <div className="fixed bottom-0 right-1/4 w-[500px] h-[500px] bg-orange-200/30 blur-[120px] rounded-full pointer-events-none" />
      
      {/* Header */}
      <header className="relative z-40 border-b border-gray-200 bg-white/80 backdrop-blur-xl sticky top-0">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-orange-500 rounded-xl flex items-center justify-center shadow-lg shadow-violet-500/25">
              <Brain className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-gray-900">Idea Fountain</span>
          </div>
          
          <nav className="hidden md:flex items-center gap-8 text-sm">
            <button onClick={() => navigate('/features')} className="text-gray-600 hover:text-gray-900 transition-colors font-medium">Features</button>
            <button onClick={() => navigate('/challenges')} className="text-gray-600 hover:text-gray-900 transition-colors font-medium">Challenges</button>
            <button onClick={() => navigate('/community')} className="text-gray-600 hover:text-gray-900 transition-colors font-medium">Community</button>
            <button onClick={() => navigate('/how-it-works')} className="text-gray-600 hover:text-gray-900 transition-colors font-medium">How It Works</button>
          </nav>
          
          <div className="flex items-center gap-3">
            {user ? (
              <>
                <Button 
                  variant="ghost" 
                  onClick={() => navigate('/dashboard')}
                  className="text-gray-700 hover:text-gray-900 hover:bg-gray-100 font-medium"
                >
                  <User className="w-4 h-4 mr-2" />
                  Dashboard
                </Button>
                <Button 
                  onClick={logout}
                  variant="ghost"
                  className="text-gray-500 hover:text-gray-900 hover:bg-gray-100"
                >
                  <LogOut className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <>
                <Button 
                  variant="ghost" 
                  onClick={() => navigate('/login')}
                  className="text-gray-700 hover:text-gray-900 hover:bg-gray-100 font-medium"
                >
                  Login
                </Button>
                <Button 
                  onClick={() => navigate('/login')}
                  className="bg-gradient-to-r from-violet-600 to-orange-500 hover:from-violet-500 hover:to-orange-400 text-white font-semibold rounded-full px-6 shadow-lg shadow-violet-500/25"
                >
                  Get Started Free
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section - Centered */}
      <section className="relative z-10 pt-24 pb-20">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-violet-100 border border-violet-200 rounded-full text-sm text-violet-700 font-medium mb-8">
            <Zap className="w-4 h-4" />
            Originality Enabling System
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold leading-[1.1] tracking-tight mb-6">
            <span className="text-gray-900">Think, Measure & Amplify</span>
            <br />
            <span className="bg-gradient-to-r from-violet-600 via-fuchsia-500 to-orange-500 bg-clip-text text-transparent">
              Creative Originality
            </span>
          </h1>
          
          <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed mb-10">
            Navigate the 6-stage pipeline to transform raw ideas into validated, 
            deployment-ready innovations. AI-powered assessment meets human creativity.
          </p>
          
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <Button 
              size="lg"
              onClick={openPersonaModal}
              className="bg-gradient-to-r from-violet-600 to-orange-500 hover:from-violet-500 hover:to-orange-400 text-white font-semibold rounded-full px-8 py-6 text-lg shadow-xl shadow-violet-500/30 hover:scale-105 transition-all duration-300"
            >
              Start an IF Session
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={() => navigate('/quick-evaluate')}
              className="border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 rounded-full px-8 py-6 text-lg transition-all font-medium"
            >
              <FileText className="mr-2 w-5 h-5" />
              Evaluate Paper
            </Button>
          </div>
          
          {/* Trust Badges */}
          <div className="flex items-center justify-center gap-6 text-sm text-gray-500">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-500" />
              <span className="font-medium">Free to start</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-500" />
              <span className="font-medium">No credit card</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-500" />
              <span className="font-medium">10 free sessions</span>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Pipeline Visualization */}
      <section className="relative z-10 py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {stages.map((stage, idx) => {
              const Icon = stage.icon;
              const isActive = idx === activeStage;
              return (
                <div 
                  key={stage.key}
                  onClick={() => setActiveStage(idx)}
                  className={`relative p-5 rounded-2xl cursor-pointer transition-all duration-500 ${
                    isActive 
                      ? `${stage.bg} ${stage.border} border-2 scale-105 shadow-lg` 
                      : 'bg-white border border-gray-200 hover:border-gray-300 hover:shadow-md'
                  }`}
                >
                  {/* Connector line */}
                  {idx < 5 && (
                    <div className="hidden lg:block absolute top-1/2 -right-2 w-4 h-0.5 bg-gradient-to-r from-gray-300 to-transparent" />
                  )}
                  
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stage.color} flex items-center justify-center mb-3 mx-auto transition-transform ${isActive ? 'scale-110' : ''}`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-center">
                    <div className={`text-xs font-mono mb-1 ${isActive ? stage.text : 'text-gray-400'}`}>
                      Stage {idx + 1}
                    </div>
                    <div className={`text-sm font-semibold ${isActive ? 'text-gray-900' : 'text-gray-700'}`}>
                      {stage.title.split(' ')[0]}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Active Stage Details */}
          <div className="mt-8 text-center">
            <div className={`inline-flex items-center gap-3 px-6 py-4 rounded-2xl ${stages[activeStage].bg} ${stages[activeStage].border} border-2 shadow-sm`}>
              {React.createElement(stages[activeStage].icon, { className: `w-6 h-6 ${stages[activeStage].text}` })}
              <div className="text-left">
                <div className="font-semibold text-gray-900">{stages[activeStage].title}</div>
                <div className="text-sm text-gray-600">{stages[activeStage].desc}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="relative z-10 py-12 border-y border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, idx) => {
              const Icon = stat.icon;
              return (
                <div key={idx} className="text-center">
                  <div className="flex justify-center mb-3">
                    <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center border border-violet-200">
                      <Icon className="w-5 h-5 text-violet-600" />
                    </div>
                  </div>
                  <div className="text-3xl md:text-4xl font-bold text-gray-900">
                    {stat.value}
                  </div>
                  <div className="text-sm text-gray-500 mt-1 font-medium">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Bento Grid */}
      <section className="relative z-10 py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <Badge className="bg-orange-100 text-orange-700 border-orange-200 mb-4 font-semibold">
              Features
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">
              Tools for Original Thinking
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* OI Card - Large */}
            <Card className="lg:col-span-2 bg-gradient-to-br from-violet-50 to-violet-100/50 border-violet-200 hover:border-violet-300 hover:shadow-lg transition-all">
              <CardContent className="p-8">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-400 to-purple-500 flex items-center justify-center mb-4">
                      <Scale className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold mb-2 text-gray-900">Originality Index</h3>
                    <p className="text-gray-600 max-w-md">
                      Measure your idea across four dimensions: Novelty, Resonance, Synthesis, and Ethical Grounding.
                    </p>
                  </div>
                  <div className="hidden md:block text-right">
                    <div className="text-6xl font-bold text-violet-200">OI</div>
                  </div>
                </div>
                <div className="mt-8 grid grid-cols-4 gap-4">
                  {['Novelty', 'Resonance', 'Synthesis', 'Ethics'].map((dim, idx) => (
                    <div key={dim} className="text-center p-3 bg-white rounded-xl border border-violet-200 shadow-sm">
                      <div className="text-2xl font-bold text-violet-600">{7 + idx * 0.5}</div>
                      <div className="text-xs text-gray-500 mt-1 font-medium">{dim}</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            
            {/* MSA Card */}
            <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100/50 border-emerald-200 hover:border-emerald-300 hover:shadow-lg transition-all">
              <CardContent className="p-8">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center mb-4">
                  <Zap className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-2 text-gray-900">Mirror-Spark</h3>
                <p className="text-gray-600 text-sm">
                  Distinguish human creativity from AI-replicable patterns.
                </p>
                <div className="mt-6 flex items-center gap-4">
                  <div className="flex-1 h-3 bg-white rounded-full overflow-hidden border border-gray-200">
                    <div className="h-full w-[35%] bg-cyan-500 rounded-full" />
                  </div>
                  <span className="text-sm text-gray-600 font-medium">35% Mirror</span>
                </div>
                <div className="mt-2 flex items-center gap-4">
                  <div className="flex-1 h-3 bg-white rounded-full overflow-hidden border border-gray-200">
                    <div className="h-full w-[65%] bg-emerald-500 rounded-full" />
                  </div>
                  <span className="text-sm text-gray-600 font-medium">65% Spark</span>
                </div>
              </CardContent>
            </Card>
            
            {/* Educator Card */}
            <Card 
              className="bg-gradient-to-br from-pink-50 to-pink-100/50 border-pink-200 hover:border-pink-300 hover:shadow-lg transition-all cursor-pointer"
              onClick={() => navigate('/educator')}
            >
              <CardContent className="p-8">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-400 to-rose-500 flex items-center justify-center mb-4">
                  <GraduationCap className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-2 text-gray-900">For Educators</h3>
                <p className="text-gray-600 text-sm">
                  Batch evaluate student papers, track class analytics, and build leaderboards.
                </p>
                <Button variant="link" className="mt-4 p-0 text-pink-600 hover:text-pink-700 font-semibold">
                  Explore Educator Tools <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </CardContent>
            </Card>
            
            {/* Challenges Card */}
            <Card 
              className="bg-gradient-to-br from-orange-50 to-orange-100/50 border-orange-200 hover:border-orange-300 hover:shadow-lg transition-all cursor-pointer"
              onClick={() => navigate('/challenges')}
            >
              <CardContent className="p-8">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center mb-4">
                  <Globe className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-2 text-gray-900">SDG Challenges</h3>
                <p className="text-gray-600 text-sm">
                  Tackle UN Sustainable Development Goals with your innovative solutions.
                </p>
                <Button variant="link" className="mt-4 p-0 text-orange-600 hover:text-orange-700 font-semibold">
                  View Challenges <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </CardContent>
            </Card>
            
            {/* Export Card */}
            <Card className="bg-white border-gray-200 hover:border-gray-300 hover:shadow-lg transition-all">
              <CardContent className="p-8">
                <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center mb-4 border border-gray-200">
                  <FileText className="w-6 h-6 text-gray-600" />
                </div>
                <h3 className="text-2xl font-bold mb-2 text-gray-900">Export Reports</h3>
                <p className="text-gray-600 text-sm">
                  Generate PDF certificates, IRB proposals, grant applications, and more.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-10 py-32 bg-gradient-to-br from-violet-600 to-orange-500">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-3xl bg-white/20 backdrop-blur mb-8">
            <Sparkles className="w-10 h-10 text-white" />
          </div>
          
          <h2 className="text-4xl md:text-6xl font-bold mb-6 text-white">
            Ready to unlock your
            <br />
            creative potential?
          </h2>
          
          <p className="text-lg text-white/90 mb-10 max-w-2xl mx-auto">
            Join thousands of innovators using Idea Fountain to validate, measure, and amplify their original ideas.
          </p>
          
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              size="lg"
              onClick={openPersonaModal}
              className="bg-white text-violet-700 hover:bg-gray-100 font-semibold rounded-full px-10 py-7 text-xl shadow-xl hover:scale-105 transition-all duration-300"
            >
              Start Free Today
              <ArrowRight className="ml-3 w-6 h-6" />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={() => navigate('/how-it-works')}
              className="border-white/50 text-white hover:bg-white/10 rounded-full px-10 py-7 text-xl font-medium"
            >
              <Play className="mr-2 w-5 h-5" />
              Watch Demo
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <AppFooter />
    </div>
  );
};

export default HomePage;
